/**
 * ReviewQueueMobileSection — one headed section of the phone queue (BON-381).
 *
 * Same two headings as the desktop, same per-section count and empty state, but
 * a stack of cards instead of a table. Open and resolved are separate TABS on a
 * phone rather than stacked blocks, so a section renders one set of rows at a
 * time and `muted` is decided by the active tab.
 */
import { CreditCard, Ticket } from 'lucide-react';

import { ReviewQueueCard } from './ReviewQueueCard';
import { sectionCopy } from './reviewQueueModel';
import type { ReviewKind, ReviewQueueItem } from './reviewTypes';

export interface ReviewQueueMobileSectionProps {
  kind: ReviewKind;
  rows: ReviewQueueItem[];
  /** True while the Resolved tab is active. */
  muted: boolean;
}

/** Same optical-centre fix as the desktop section header: `.nb-display` is
 *  uppercase, so the heading's box is collapsed onto its em box and the row
 *  centres, instead of letting a group led by an SVG synthesize its baseline
 *  from the icon's bottom edge. */
const HEADER_ROW = {
  display: 'flex',
  alignItems: 'center',
  gap: 7,
  color: 'var(--nb-bark-dark)',
} as const;

const ICON = { flex: '0 0 14px', display: 'block' } as const;

const TITLE = { margin: 0, fontSize: 15, lineHeight: 1 } as const;

export function ReviewQueueMobileSection({ kind, rows, muted }: ReviewQueueMobileSectionProps) {
  const copy = sectionCopy(kind);
  const Icon = kind === 'booking' ? Ticket : CreditCard;

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
      <div style={HEADER_ROW}>
        <Icon size={14} style={ICON} />
        <h2 className="nb-display" style={TITLE}>
          {copy.title}
        </h2>
        <span
          data-testid="section-count"
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: rows.length && !muted ? 'var(--nb-warn)' : 'var(--nb-subtle)',
          }}
        >
          {rows.length}
        </span>
      </div>

      {rows.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {rows.map((item) => (
            <ReviewQueueCard key={item.id} item={item} muted={muted} />
          ))}
        </div>
      ) : (
        <div
          style={{
            padding: '20px 14px',
            borderRadius: 13,
            border: '1px solid var(--nb-border)',
            background: 'var(--nb-bg)',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: 12.5, color: 'var(--nb-subtle)' }}>
            {muted ? 'Nothing resolved yet.' : copy.emptyBody}
          </div>
        </div>
      )}
    </section>
  );
}
